import React from 'react';
import { getRawDateString } from '../../authConfig';
import { 
  FaSearch, 
  FaUser, 
  FaPlus, 
  FaFileDownload, 
  FaSync, 
  FaFilter, 
  FaCalendarAlt, 
  FaListOl,
  FaTimes,
  FaUndo
} from 'react-icons/fa';

const TaxPortalToolbar = ({
  searchPlaceholder = "Search clients, IDs, or details...",
  searchText,
  onSearchChange,
  
  statusOptions,
  filterStatus,
  onFilterStatusChange,
  
  assignedToOptions,
  filterAssignedTo,
  onFilterAssignedToChange,
  
  dueDateOptions,
  filterDueDate,
  onFilterDueDateChange,
  
  pageSize,
  onPageSizeChange,
  
  onAdd,
  addLabel = "Add Record",
  
  onExport,
  disableExport = false,
  
  onRefresh,
  isRefreshing = false,
  theme = 'default'
}) => {
  const isGlass = theme === 'glass';
  
  const hasActiveFilters = Boolean(
    searchText ||
    (filterStatus && filterStatus !== 'nonCompleted') ||
    filterAssignedTo ||
    filterDueDate
  );

  const resetAllFilters = () => {
    if (onSearchChange) onSearchChange('');
    if (onFilterStatusChange) onFilterStatusChange('nonCompleted');
    if (onFilterAssignedToChange) onFilterAssignedToChange('');
    if (onFilterDueDateChange) onFilterDueDateChange('');
  };

  return (
    <div className={`card mb-4 border-0 rounded-4 shadow-sm overflow-hidden ${isGlass ? 'glass-card' : 'bg-white'}`} style={{ border: '1px solid #e2e8f0', boxShadow: '0 10px 30px -5px rgba(15,23,42,0.06)' }}>
      
      {/* --- TOP SECTION: Search & Primary Actions --- */}
      <div className="p-4 pb-3">
        <div className="row g-3 align-items-center justify-content-between">
          
          {/* Main Search */}
          <div className="col-12 col-md-6 col-lg-5">
            <div className={`input-group rounded-pill overflow-hidden border shadow-sm ${isGlass ? 'glass-input border-0' : 'bg-white'}`} style={{ transition: 'all 0.25s ease', borderColor: searchText ? '#0284c7' : '#e2e8f0' }}>
              <span className={`input-group-text border-0 ps-3 pe-2 ${isGlass ? 'bg-transparent text-dark' : 'bg-white text-muted'}`}>
                <FaSearch size={14} style={{ color: '#0284c7' }} />
              </span>
              <input
                type="text"
                className={`form-control border-0 px-2 shadow-none py-2 ${isGlass ? 'bg-transparent text-dark' : 'bg-white text-dark'}`}
                placeholder={searchPlaceholder}
                value={searchText}
                onChange={(e) => onSearchChange(e.target.value)}
                style={{ fontSize: '0.9rem', fontWeight: '500' }}
              />
              {searchText && (
                <button 
                  className="btn border-0 bg-transparent text-muted pe-3 ps-1"
                  onClick={() => onSearchChange('')}
                  title="Clear search"
                  type="button"
                >
                  <FaTimes size={13} />
                </button>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="col-12 col-md-auto">
            <div className="d-flex flex-wrap gap-2 justify-content-start justify-content-md-end">
              {onAdd && (
                <button className="btn btn-primary rounded-pill px-4 py-2 fw-bold shadow-sm d-flex align-items-center gap-2 hover-lift" onClick={onAdd} style={{ background: 'linear-gradient(135deg, #0284c7, #2563eb)', border: 'none' }}>
                  <FaPlus size={12} /> {addLabel}
                </button>
              )}
              {onExport && (
                <button className="btn btn-outline-success rounded-pill px-3 py-2 fw-bold shadow-sm d-flex align-items-center gap-2 hover-lift" onClick={onExport} disabled={disableExport}>
                  <FaFileDownload size={12} /> Export
                </button>
              )}
              {onRefresh && (
                <button className="btn btn-light border rounded-pill px-3 py-2 fw-bold shadow-sm d-flex align-items-center gap-2 text-secondary hover-lift" onClick={onRefresh} disabled={isRefreshing}>
                  <FaSync size={12} className={isRefreshing ? "fa-spin text-primary" : ""} /> {isRefreshing ? 'Refreshing...' : 'Refresh'}
                </button>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* --- BOTTOM SECTION: Refinement Filters --- */}
      <div className={`px-4 py-3 border-top ${isGlass ? 'bg-transparent' : 'bg-light-subtle'}`} style={{ borderColor: '#f1f5f9' }}>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.7px' }}>
            <FaFilter size={11} style={{ color: '#0284c7' }} /> Refine Results
          </div>
          {hasActiveFilters && (
            <button
              className="btn btn-link btn-sm p-0 text-decoration-none text-danger fw-semibold d-flex align-items-center gap-1"
              onClick={resetAllFilters}
              style={{ fontSize: '11px' }}
            >
              <FaUndo size={10} /> Reset Filters
            </button>
          )}
        </div>
        
        <div className="row g-2">
          {/* Status Filter */}
          {statusOptions && (
            <div className="col-12 col-sm-6 col-md-3">
              <div className={`input-group input-group-sm rounded-pill overflow-hidden border shadow-sm ${isGlass ? 'glass-input border-0' : 'bg-white'}`} style={{ borderColor: filterStatus && filterStatus !== 'nonCompleted' ? '#0284c7' : '#e2e8f0' }}>
                <span className={`input-group-text border-0 ps-3 pe-1 ${isGlass ? 'bg-transparent text-dark' : 'bg-white text-muted'}`}><FaFilter size={11} style={{ color: filterStatus && filterStatus !== 'nonCompleted' ? '#0284c7' : '#94a3b8' }} /></span>
                <select 
                  className={`form-select border-0 shadow-none py-2 ${isGlass ? 'bg-transparent text-dark' : 'bg-white text-dark'}`} 
                  value={filterStatus} 
                  onChange={(e) => onFilterStatusChange(e.target.value)} 
                  style={{ cursor: 'pointer', fontSize: '0.83rem', fontWeight: '600' }}
                >
                  <option value="nonCompleted">Open / Incomplete</option>
                  <option value="">All Statuses</option>
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Assignee Filter */}
          {assignedToOptions && (
            <div className="col-12 col-sm-6 col-md-3">
              <div className={`input-group input-group-sm rounded-pill overflow-hidden border shadow-sm ${isGlass ? 'glass-input border-0' : 'bg-white'}`} style={{ borderColor: filterAssignedTo ? '#0284c7' : '#e2e8f0' }}>
                <span className={`input-group-text border-0 ps-3 pe-1 ${isGlass ? 'bg-transparent text-dark' : 'bg-white text-muted'}`}><FaUser size={11} style={{ color: filterAssignedTo ? '#0284c7' : '#94a3b8' }} /></span>
                <select
                  className={`form-select border-0 shadow-none py-2 ${isGlass ? 'bg-transparent text-dark' : 'bg-white text-dark'}`}
                  value={filterAssignedTo}
                  onChange={(e) => onFilterAssignedToChange(e.target.value)}
                  style={{ cursor: 'pointer', fontSize: '0.83rem', fontWeight: '600' }}
                >
                  <option value="">All Assignees</option>
                  {assignedToOptions.map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Due Date Filter */}
          {dueDateOptions && (
            <div className="col-12 col-sm-6 col-md-3">
              <div className={`input-group input-group-sm rounded-pill overflow-hidden border shadow-sm ${isGlass ? 'glass-input border-0' : 'bg-white'}`} style={{ borderColor: filterDueDate ? '#0284c7' : '#e2e8f0' }}>
                <span className={`input-group-text border-0 ps-3 pe-1 ${isGlass ? 'bg-transparent text-dark' : 'bg-white text-muted'}`}><FaCalendarAlt size={11} style={{ color: filterDueDate ? '#0284c7' : '#94a3b8' }} /></span>
                <select 
                  className={`form-select border-0 shadow-none py-2 ${isGlass ? 'bg-transparent text-dark' : 'bg-white text-dark'}`} 
                  value={filterDueDate} 
                  onChange={(e) => onFilterDueDateChange(e.target.value)} 
                  style={{ cursor: 'pointer', fontSize: '0.83rem', fontWeight: '600' }}
                >
                  <option value="">Any Due Date</option>
                  {dueDateOptions.map((date) => (
                    <option key={date} value={date}>{getRawDateString(date)}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Page Size Filter */}
          {pageSize !== undefined && onPageSizeChange && (
            <div className="col-12 col-sm-6 col-md-3">
               <div className={`input-group input-group-sm rounded-pill overflow-hidden border shadow-sm ${isGlass ? 'glass-input border-0' : 'bg-white'}`}>
                <span className={`input-group-text border-0 ps-3 pe-1 ${isGlass ? 'bg-transparent text-dark' : 'bg-white text-muted'}`}><FaListOl size={11} /></span>
                <select 
                  className={`form-select border-0 shadow-none py-2 ${isGlass ? 'bg-transparent text-dark' : 'bg-white text-dark'}`} 
                  value={pageSize} 
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  style={{ cursor: 'pointer', fontSize: '0.83rem', fontWeight: '600' }}
                >
                  <option value={25}>25 items per page</option>
                  <option value={100}>100 items per page</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaxPortalToolbar;